import BaseResource from './index'
import CompanyResource from './company'
import LookingForResource from '@/resources/lookingfor'
import MetroAreaResource from './metroarea'
import SkillResource from './skill'
import KeywordResource from './keyword'
import MarketResource from './market'
import RoleResource from './role'
import { uuidWeak } from '../util/uuid'

export default class ListingResource extends BaseResource {
    static endpoint = '/listings'

    static related() {
        return {
            company: CompanyResource,
            role: RoleResource,
            type: LookingForResource,
            regions: MetroAreaResource,
            skills: SkillResource,
            skills_required: SkillResource,
            keywords: KeywordResource,
            markets: MarketResource,
        }
    }

    static defaults = {
        location: '',
        body: '',
        compensation_min: '',
        compensation_max: '',
        equity_percentage_min: '',
        equity_percentage_max: '',
        apply_link_external: '',
        remote_level: 0,
        slug() {
            return uuidWeak()
        },
    }

    // Keep track of any properties that will invalidate this._status
    static propertiesChangeStatus = ['is_draft', 'is_archived', 'expires']

    static async getUserApplications(options = {}) {
        let wrapped = this.wrap('/applications/mine')
        let result = await wrapped.get(options)
        let conneg = this.client.negotiateContent(this, { listFn: ListingResource.getUserApplications })
        return conneg(result)
    }

    static async active(options = {}) {
        let wrapped = this.wrap('/active')
        let result = await wrapped.get(options)
        let conneg = this.client.negotiateContent(this, { listFn: ListingResource.active })
        return conneg(result)
    }

    get compensationMin() {
        return Number(this.attributes.compensation_min).toLocaleString()
    }

    get compensationMax() {
        return Number(this.attributes.compensation_max).toLocaleString()
    }

    getExpiresDate() {
        return new Date(this.attributes.expires)
    }

    // If any of the properties in propertiesChangeStatus change, invalidate this._status
    toInternalValue(...args) {
        if(~ListingResource.propertiesChangeStatus.indexOf(args[0])) {
            this._status = undefined
        }

        return super.toInternalValue(...args)
    }

    // Getter for details on listing status
    get status() {
        if(this._status) {
            return this._status
        }

        if(this.attributes.is_archived) {
            this._status = {
                variant: 'secondary',
                text: 'Archived'
            }
        } else if(this.attributes.expires && this.getExpiresDate() < new Date()) {
            this._status = {
                variant: 'warning',
                text: 'Expired'
            }
        } else if(this.attributes.expires && this.getExpiresDate() > new Date()) {
            this._status = {
                variant: 'success',
                text: this.attributes.is_paused ? 'Paused': 'Active'
            }
        } else {
            this._status = {
                variant: 'secondary',
                text: 'Draft'
            }
        }

        return this._status
    }

    async activate() {
        let activateWrapped = this.wrap('/activate')
        let result = await activateWrapped.post()
        if(result.status === 200) {
            this.set('expires', result.data.expires)
            return true
        } else {
            return false
        }
    }

    async interestAction(profileId, interested = true) {
        let interestWrapped = this.wrap('/interest')
        let result = await interestWrapped.post({ interested, profile: profileId })
        if(result.status === 200) {
            return true
        }
        return false
    }

    pass(profileId) {
        return this.interestAction(profileId, false)
    }

    interested(profileId) {
        return this.interestAction(profileId, true)
    }

    async apply() {
        let wrapped = this.wrap('/apply')
        let result = await wrapped.post()
        
        if(result.status === 200) {
            return true
        }

        return false
    }

    async getRelatedPostings() {
        let wrapped = this.wrap('/similar')
        let result = await wrapped.get()
        let conneg = ListingResource.client.negotiateContent(ListingResource, { listFn: ListingResource.getRelatedPostings })
        return conneg(result)
    }

    getCompensationPretty() {
        let parts = [this.compensationMin, this.compensationMax]
        return parts.filter((part) => !!part).map((part) => `$${part}`).join(' - ')
    }

    getEquityPretty() {
        let parts = [this.get('equity_percentage_min'), this.get('equity_percentage_max')]
        return parts.filter((part) => !!part).map((part) => `${part}%`).join(' to ')
    }

    toString() {
        return this.attributes.title
    }
}
